import {
  MAX_IMPORT_BODY_BYTES,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_ROWS,
  MAX_STAGED_IMPORT_ROWS,
  type TransferColumn,
} from '@palaccount/contracts';
import { parseCsv, readXlsx, SpreadsheetError, type Grid } from '@palaccount/spreadsheet';
import type { TFunction } from 'i18next';

/**
 * ADM-03's import, browser side: a dropped file into the grid the server takes.
 *
 * ── WHY THE PARSING IS HERE AND NOT ON THE SERVER ─────────────────────────
 *
 * The same split the export half chose, for the same reason, running the other
 * way. The export returns a GRID and the client writes the file, because the
 * client is what knows the reader's language and what a numeric cell should
 * become. The import reverses it: the client reads the file and sends a grid.
 *
 * The deciding fact is the HEADING ROW. `writeFile` writes translated headings
 * (`impexp.col.<key>`), so an Arabic user's template is Arabic — and the
 * translation table is the client's. A server matching an uploaded heading
 * against a column key would have to hold every language's labels, which is a
 * second translation catalogue and a second thing to forget when a translator
 * improves a word. So the client maps the labels back to KEYS, which is a pure
 * inverse of what it wrote, and the server checks the keys.
 *
 * What that does NOT move to the client is anything that matters:
 * `assertColumns` on the server re-checks every key and its POSITION and
 * refuses with ERR-ADM-03-01, so a heading that mapped to nothing arrives
 * verbatim and is refused by position rather than quietly dropped. A caller
 * hitting the API directly sends keys and is checked identically.
 */

export class ImportFileError extends Error {}

/**
 * THE ONE SENTENCE ON THIS SCREEN THE MESSAGE MAP CANNOT CARRY, and the reason
 * is worth stating where somebody will meet it.
 *
 * `STATIC_MESSAGE_KEYS` maps WHOLE SENTENCES to i18n keys by EXACT MATCH. That
 * works for a fixed string and it works for a function called with the one
 * argument the whole product uses (`PO_MESSAGES.approvalRequired()` is mapped
 * that way). It cannot work here, because this file calls
 * `DATA_TRANSFER_MESSAGES.tooManyRows` with TWO different ceilings — 5,000 for
 * one request and 50,000 for one import — so there is no single sentence to
 * key on. Mapping either one would silently leave the other in English.
 *
 * Nor can the map be taught the trick: `localizeServerMessage` calls `t(key)`
 * with no arguments, so pointing an entry at an ICU key like
 * `impexp.tooManyRows` would render the literal text `{max}` on screen. The
 * dictionary key is therefore addressed HERE, where the argument is known.
 *
 * The number is formatted `en-US` because that is what the contract's own
 * sentence does. A `{max, number}` ICU skeleton would format per-locale and is
 * arguably better, but choosing it HERE would set a dictionary-wide convention
 * as a side effect of a localisation fix — which is how conventions get set by
 * accident. That is a decision for whoever owns the numeral convention, not a
 * claim about what the dictionary currently contains.
 */
function tooManyRows(t: TFunction, max: number): string {
  return t('impexp.tooManyRows', { max: max.toLocaleString('en-US') });
}

/** BR-ADM-03-10, checked where the BYTES are — the server never sees them. */
export function assertUnderLimit(file: File, t: TFunction): void {
  if (file.size > MAX_IMPORT_BYTES) {
    throw new ImportFileError(t('impexp.tooLarge'));
  }
}

/**
 * The other ceiling, and the one that actually bites first.
 *
 * The grid travels as a request BODY, and the platform's body limit is 1 MiB
 * (see `MAX_IMPORT_ROWS` for why this build does not raise it for one screen).
 * A payload past that would come back as a bare 413 with nothing on screen to
 * explain it, so the browser — which is the only thing that can see the size
 * before it is sent — refuses it with the same sentence the server would use.
 *
 * Checked on the SERIALISED payload rather than on the row count, because a
 * thousand rows of long partner names and addresses is a different number of
 * bytes from a thousand rows of item codes.
 */
export function assertBodyFits(rows: readonly (readonly string[])[], t: TFunction): void {
  if (!bodyFits(rows)) throw new ImportFileError(tooManyRows(t, MAX_IMPORT_ROWS));
}

/** The predicate on its own — `needsStaging` wants the answer, not a sentence. */
function bodyFits(rows: readonly (readonly string[])[]): boolean {
  return rows.length <= MAX_IMPORT_ROWS && JSON.stringify(rows).length <= MAX_IMPORT_BODY_BYTES;
}

/**
 * §15's ceiling, checked in the browser before a single chunk is sent.
 *
 * The server refuses the same file when the run is opened — the browser is not
 * the control, it is the courtesy. A person holding a 60,000-row spreadsheet
 * should be told in the moment they drop it, not after fifty round trips.
 */
export function assertStagedFits(rows: readonly (readonly string[])[], t: TFunction): void {
  if (rows.length > MAX_STAGED_IMPORT_ROWS) {
    throw new ImportFileError(tooManyRows(t, MAX_STAGED_IMPORT_ROWS));
  }
}

/**
 * HOW BIG A CHUNK CAN BE, in rows, for THIS file.
 *
 * Not a constant, because the thing that bites is BYTES: a thousand rows of
 * partner names and street addresses is several times the payload of a
 * thousand rows of item codes. So the size is derived from the file's own
 * average row, kept under the body limit with margin, and capped at the
 * per-request row ceiling.
 *
 * At least one row, always — a single row wider than the body limit is a
 * problem the server must refuse with a sentence, not something the client
 * should loop forever failing to send.
 */
export function chunkSize(rows: readonly (readonly string[])[]): number {
  if (rows.length === 0) return MAX_IMPORT_ROWS;
  const sample = rows.slice(0, 50);
  const averageBytes = Math.max(1, Math.ceil(JSON.stringify(sample).length / sample.length));
  const byBytes = Math.floor(MAX_IMPORT_BODY_BYTES / averageBytes);
  return Math.max(1, Math.min(MAX_IMPORT_ROWS, byBytes));
}

/**
 * Whether this file needs the staged path at all.
 *
 * The small path is not a fallback, it is the ordinary case: §1's own example
 * is "load A THOUSAND ITEMS" and §13's walkthrough is 400 rows, and neither
 * should pay for a staging table and a second round trip. A file that fits in
 * one body goes in one body.
 */
export function needsStaging(rows: readonly (readonly string[])[]): boolean {
  return !bodyFits(rows);
}

/**
 * The file, as rows of strings.
 *
 * Chosen by EXTENSION rather than by MIME type: browsers disagree about what
 * an .xlsx is (`application/vnd.…sheet`, `application/octet-stream`, and on
 * some Windows machines nothing at all), and a person who renamed a file is
 * telling you what they think it is.
 */
export async function readImportFile(file: File, t: TFunction): Promise<Grid> {
  const name = file.name.toLowerCase();
  try {
    if (name.endsWith('.csv') || name.endsWith('.txt')) {
      return parseCsv(await file.text());
    }
    if (name.endsWith('.xlsx')) {
      return readXlsx(new Uint8Array(await file.arrayBuffer()));
    }
  } catch (error) {
    throw new ImportFileError(
      error instanceof SpreadsheetError ? error.message : t('impexp.unreadable'),
    );
  }
  throw new ImportFileError(t('impexp.wrongFileType'));
}

/**
 * The heading row, back to column keys — the exact inverse of `writeFile`.
 *
 * Matched case- and space-insensitively, because a person who typed the
 * headings themselves rather than downloading the template will not have
 * matched the capitals, and refusing "unit price" against "Unit price" would
 * be pedantry about something unambiguous. The KEY ITSELF is also accepted, so
 * a file produced by a script or by an earlier API caller reads too.
 *
 * A heading that matches nothing is passed through UNCHANGED. That is
 * deliberate: the server then refuses it by position with ERR-ADM-03-01's
 * sentence ("Column 3 should be `SKU`"), which tells the person where to look.
 * Silently dropping it, or guessing, would produce a file that imports into the
 * wrong columns.
 */
export function headersToKeys(
  headers: readonly string[],
  columns: readonly TransferColumn[],
  t: TFunction,
): string[] {
  const normalise = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');
  const byLabel = new Map<string, string>();
  for (const column of columns) {
    byLabel.set(normalise(t(`impexp.col.${column.key}`)), column.key);
    byLabel.set(normalise(column.key), column.key);
  }
  return headers.map((heading) => byLabel.get(normalise(heading)) ?? heading.trim());
}

/**
 * BR-ADM-03-03 — "the rows that were refused, with the reason on each one."
 *
 * The refused rows are the WHOLE FILE, because the import is all-or-nothing;
 * what the person needs back is their own sheet with a Reason column bolted
 * on, so they can fix it in place and re-upload. Rows with no problem carry an
 * empty reason rather than being dropped — a file that came back shorter than
 * it went in is a file somebody has to reconcile by hand.
 */
export function refusedRowsFile(
  columns: readonly TransferColumn[],
  rows: readonly (readonly string[])[],
  problems: readonly { row: number | null; column: string | null; message: string }[],
  t: TFunction,
): { headers: string[]; rows: string[][] } {
  const reasons = new Map<number, string[]>();
  for (const problem of problems) {
    if (problem.row === null) continue;
    const label =
      problem.column === null ? '' : `${t(`impexp.col.${problem.column}`)}: `;
    reasons.set(problem.row, [...(reasons.get(problem.row) ?? []), `${label}${problem.message}`]);
  }
  return {
    headers: [...columns.map((column) => t(`impexp.col.${column.key}`)), t('impexp.reason')],
    // The header is row 1, so the first record is row 2 — the same numbering
    // every message on this screen uses.
    rows: rows.map((row, index) => [...row, (reasons.get(index + 2) ?? []).join(' · ')]),
  };
}
